import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AllGallery.css';
import { useTheme } from '../contexts/ThemeContext';
import galleryData from '../data/gallery.json';
import { useContent } from '../contexts/ContentContext';

type GalleryItem = {
  id: number;
  title: string;
  description: string;
  category: string;
  image: string;
  featured: boolean;
};

const AllGallery = () => {
  const { getSection } = useContent();
  const data = getSection<typeof galleryData>('gallery', galleryData);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    setGalleryItems(data.gallery);
  }, [data]);

  const openLightbox = (item: GalleryItem) => {
    setSelectedImage(item);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const currentIndex = galleryItems.findIndex(item => item.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % galleryItems.length;
    setSelectedImage(galleryItems[nextIndex]);
  };

  const prevImage = () => {
    const currentIndex = galleryItems.findIndex(item => item.id === selectedImage.id);
    const prevIndex = (currentIndex - 1 + galleryItems.length) % galleryItems.length;
    setSelectedImage(galleryItems[prevIndex]);
  };

  const handleBackToHome = () => {
    navigate('/');
    setTimeout(() => {
      const gallerySection = document.getElementById('gallery');
      if (gallerySection) {
        gallerySection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div className={`all-gallery-page ${theme}`}>
      {/* Header Section */}
      <div className="all-gallery-header">
        <div className="header-content">
          <button className="back-button" onClick={handleBackToHome}>
            <span className="back-icon">←</span>
            Back to Home
          </button>
          <h1 className="page-title">
            <span className="title-accent">Complete</span> Gallery
          </h1>
          <p className="page-subtitle">
            Explore all moments of innovation, learning, and collaboration at ASIMO Technical Club
          </p>
        </div>
      </div>

      {/* Gallery Content */}
      <div className="all-gallery-container">
        {/* Gallery Grid */}
        <div className="gallery-grid">
          {galleryItems.map(item => (
            <div 
              key={item.id} 
              className={`gallery-item ${item.featured ? 'featured' : ''}`}
              onClick={() => openLightbox(item)}
            >
              <div className="gallery-card">
                <div className="image-container">
                  <img 
                    src={`/assets/images/gallery/main page/${item.image.split('/').pop()}`}
                    alt={item.title}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = `/assets/images/gallery/gallery extended/${item.image.split('/').pop()}`;
                    }}
                  />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <h3 className="image-title">{item.title}</h3>
                      <p className="image-description">{item.description}</p>
                      <span className="image-category">{item.category}</span>
                    </div>
                  </div>
                </div>
                {item.featured && (
                  <div className="featured-badge">
                    <span>★ Featured</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {galleryItems.length === 0 && (
          <div className="empty-state">
            <div className="empty-content">
              <span className="empty-icon">📷</span>
              <h3>No photos found</h3>
              <p>Try selecting a different category to view more photos.</p>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <span>×</span>
            </button>
            
            <button className="lightbox-nav prev" onClick={prevImage}>
              <span>‹</span>
            </button>
            
            <div className="lightbox-content">
              <img 
                src={`/assets/images/gallery/main page/${selectedImage.image.split('/').pop()}`}
                alt={selectedImage.title}
                onError={(e) => {
                  e.currentTarget.src = `/assets/images/gallery/gallery extended/${selectedImage.image.split('/').pop()}`;
                }}
              />
              <div className="lightbox-info">
                <h3>{selectedImage.title}</h3>
                <p>{selectedImage.description}</p>
                <div className="lightbox-meta">
                  <span className="lightbox-category">{selectedImage.category}</span>
                  {selectedImage.featured && <span className="lightbox-featured">★ Featured</span>}
                </div>
              </div>
            </div>
            
            <button className="lightbox-nav next" onClick={nextImage}>
              <span>›</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllGallery;